/**
 * PDF Verification API Actions
 * 
 * Calls the backend to verify PDF encryption status using qpdf.
 * This provides reliable detection of password-protected PDFs
 * that matches what the backend processing tools can handle.
 */

import axiosInstance from "../utils/axiosInstance";

export interface VerifyPdfResult {
  /** Whether the PDF is encrypted */
  encrypted: boolean;
  /** Whether a password is required to process the PDF */
  needsPassword: boolean;
  /** If a password was provided, whether it was valid */
  passwordValid?: boolean;
  /** Error message if verification failed */
  error?: string;
}

export interface VerifyPdfRequest {
  /** S3 key of the uploaded PDF file */
  s3Key: string;
  /** Optional password to validate */
  password?: string;
}

/**
 * Verify a single PDF file's encryption status.
 * For verifying multiple PDFs, prefer verifyMultiplePdfs which uses
 * a single batch API call instead of N individual requests.
 * 
 * @param s3Key - The S3 key of the uploaded PDF file
 * @param password - Optional password to validate
 * @returns Verification result including encryption status
 */
export async function verifyPdf(
  s3Key: string,
  password?: string
): Promise<VerifyPdfResult> {
  try {
    const requestBody: VerifyPdfRequest = { s3Key };
    
    if (password) {
      requestBody.password = password;
    }
    
    const response = await axiosInstance.post<VerifyPdfResult>(
      `/utility/verify-pdf`,
      requestBody
    );
    
    return response.data;
  } catch (error: any) {
    console.error("Error verifying PDF:", error.response?.data || error.message);
    
    if (error.response?.status === 403) {
      throw new Error("Access denied to this file");
    }
    
    // Return a default response so the upload can proceed with frontend-only detection
    return {
      encrypted: false,
      needsPassword: false,
      error: error.response?.data?.message || "Failed to verify PDF"
    };
  }
}

/**
 * Verify multiple PDF files in a single batch API call.
 * This replaces N individual verifyPdf calls with one POST /utility/verify-pdfs request.
 * 
 * @param s3Keys - Array of S3 keys for uploaded PDF files
 * @returns Map of S3 key to verification result
 */
export async function verifyMultiplePdfs(
  s3Keys: string[]
): Promise<Map<string, VerifyPdfResult>> {
  const resultMap = new Map<string, VerifyPdfResult>();
  
  if (s3Keys.length === 0) {
    return resultMap;
  }

  try {
    const response = await axiosInstance.post<{ results: Array<VerifyPdfResult & { s3Key: string }> }>(
      `/utility/verify-pdfs`,
      { s3Keys }
    );

    for (const item of response.data.results) {
      resultMap.set(item.s3Key, {
        encrypted: item.encrypted,
        needsPassword: item.needsPassword,
        passwordValid: item.passwordValid,
        error: item.error
      });
    }
  } catch (error: any) {
    console.error("Error batch-verifying PDFs:", error.response?.data || error.message);

    if (error.response?.status === 403) {
      throw new Error("Access denied to one or more files");
    }

    // On batch failure, default all keys to safe values so the upload can continue
    // with frontend-only detection rather than blocking the user entirely.
    for (const s3Key of s3Keys) {
      resultMap.set(s3Key, {
        encrypted: false,
        needsPassword: false,
        error: error.response?.data?.message || "Failed to verify PDF"
      });
    }
  }
  
  return resultMap;
}

/**
 * Validate a password for a specific PDF using the single-file endpoint.
 * 
 * @param s3Key - The S3 key of the uploaded PDF file
 * @param password - The password to validate
 * @returns Whether the password is valid
 */
export async function validatePdfPassword(
  s3Key: string,
  password: string
): Promise<{ valid: boolean; error?: string }> {
  const result = await verifyPdf(s3Key, password);
  
  if (result.passwordValid === true) {
    return { valid: true };
  }
  
  return {
    valid: false,
    error: result.error || "Incorrect password"
  };
}
